/**
 * ClerkSync – bridges Clerk authentication with the PHP backend.
 *
 * On every page load (including refreshes), when Clerk says the user is
 * signed-in, this calls /api/auth/clerk-sync.php to find/create the user
 * in MySQL and return a fresh PHP JWT with the correct role.
 *
 * FIX: Removed the `!isAuthenticated` guard so that stale localStorage
 *      data (e.g. role: 'doctor') is always overwritten by the live DB value.
 *      De-duplication is handled by `syncedRef` (keyed per Clerk user ID).
 */
import { useEffect, useRef } from 'react'
import { useUser } from '@clerk/react'
import { useAuthStore } from '../hooks/useAuthStore'

export default function ClerkSync() {
  const { user, isSignedIn, isLoaded } = useUser()
  const { logout } = useAuthStore()

  // Track which Clerk user ID we last synced so we don't double-call
  const syncedForId = useRef(null)

  useEffect(() => {
    if (!isLoaded) return

    // ── User is signed in via Clerk – always sync to get fresh role ──
    if (isSignedIn && user && syncedForId.current !== user.id) {
      syncedForId.current = user.id

      const email = user.primaryEmailAddress?.emailAddress ?? ''
      const name =
        user.fullName ||
        [user.firstName, user.lastName].filter(Boolean).join(' ') ||
        email

      fetch('/api/auth/clerk-sync.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clerk_id: user.id, email, name }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.success) {
            // Overwrite Zustand store with fresh user + role from DB
            useAuthStore.setState({
              user: data.user,      // includes role: 'admin' | 'doctor' | 'patient'
              token: data.token,
              isAuthenticated: true,
            })
          } else {
            console.error('[ClerkSync] Backend sync failed:', data.error)
            syncedForId.current = null // allow retry
          }
        })
        .catch((err) => {
          console.error('[ClerkSync] Network error:', err)
          syncedForId.current = null // allow retry
        })
    }

    // ── User signed out of Clerk → clear backend state ──
    if (!isSignedIn && isLoaded) {
      syncedForId.current = null
      logout()
    }
  }, [isSignedIn, isLoaded, user, logout])

  return null
}
